// Multi-state medical license helpers.
//
// Providers (MD/DO/DPM/ARNP/PA) frequently hold licenses in multiple states.
// We store them as an ordered array on `custom.licenses`:
//
//   custom.licenses = [
//     { state: "FL", licenseNumber: "A12345", primary: true },
//     { state: "GA", licenseNumber: "B98765" },
//   ]
//
// BACKWARD COMPATIBILITY: `custom.licenseNumber` (the legacy single string) is
// kept as a MIRROR of the primary license's number so every existing reader
// (SignerContactCard, the backend, order PDFs) keeps working untouched. The
// caller is responsible for writing that mirror via getPrimaryLicenseNumber()
// whenever it writes the array.
//
// `custom.practiceState` is INTENTIONALLY independent (it drives DME MAC
// jurisdiction routing) and is never derived from or written by these helpers.

const toCode = (s) =>
  String(s ?? "")
    .trim()
    .toUpperCase();
const toNumber = (s) => String(s ?? "").trim();

// Guarantee exactly one `primary: true` entry (the first flagged one, or the
// first entry when none/garbage is flagged). Empty array stays empty.
function withPrimaryInvariant(licenses) {
  if (!licenses.length) return [];
  const flagged = licenses.findIndex((l) => l.primary);
  const primaryIdx = flagged === -1 ? 0 : flagged;
  return licenses.map((l, i) => ({ ...l, primary: i === primaryIdx }));
}

// Normalize whatever is stored on `custom` into a clean, display-ready array.
// - If `custom.licenses` exists, sanitize it: trim/upper state codes, drop
//   empty-number rows, dedupe by state (first wins), enforce one primary.
// - Else, derive-on-load from the legacy `custom.licenseNumber` string so old
//   profiles "just work" with no backend backfill.
// - Else, empty array.
export function normalizeLicenses(custom) {
  const c = custom || {};

  if (Array.isArray(c.licenses)) {
    const seen = new Set();
    const cleaned = [];
    c.licenses.forEach((l) => {
      if (!l) return;
      const state = toCode(l.state);
      const licenseNumber = toNumber(l.licenseNumber);
      if (!licenseNumber) return; // skip blank rows
      // A row without a state is keyed by its number so multiple are possible,
      // but a real state code collapses duplicates to the first occurrence.
      const key = state || `__nostate__${licenseNumber}`;
      if (seen.has(key)) return;
      seen.add(key);
      cleaned.push({ state, licenseNumber, primary: !!l.primary });
    });
    return withPrimaryInvariant(cleaned);
  }

  const legacy = toNumber(c.licenseNumber);
  if (legacy) {
    // Seed the legacy single license with NO state. practiceState drives DME MAC
    // jurisdiction routing and is NOT necessarily the state the license was issued
    // in — deriving the license state from it would silently stamp wrong metadata
    // onto the new structured field. Leave it blank for the provider to fill in
    // deliberately; the legacy licenseNumber mirror keeps downstream readers intact.
    return [{ state: "", licenseNumber: legacy, primary: true }];
  }

  return [];
}

// The value mirrored into `custom.licenseNumber` for backward compatibility.
export function getPrimaryLicenseNumber(licenses) {
  if (!Array.isArray(licenses) || !licenses.length) return "";
  const primary = licenses.find((l) => l.primary);
  return (primary || licenses[0]).licenseNumber || "";
}

// Write-side helper for the controlled MedicalLicenseManager: persist the
// licenses array AND mirror the primary number into the legacy
// `custom.licenseNumber` string so downstream readers stay intact. Takes the
// host's `setProfileCustomValue(key) => (event) => void` setter — the same
// shape used by Settings, UserEdit, and the onboarding wizard.
export function applyLicenses(setProfileCustomValue, licenses) {
  setProfileCustomValue("licenses")({ target: { value: licenses } });
  setProfileCustomValue("licenseNumber")({ target: { value: getPrimaryLicenseNumber(licenses) } });
}

// Add a license, or UPDATE the existing row when the state already exists
// (one license per state — the locked product decision). Returns a new array.
export function upsertLicense(licenses, { state, licenseNumber } = {}) {
  const base = (Array.isArray(licenses) ? licenses : []).map((l) => ({ ...l }));
  const code = toCode(state);
  const num = toNumber(licenseNumber);
  if (!num) return withPrimaryInvariant(base); // nothing meaningful to add

  const idx = base.findIndex((l) => toCode(l.state) === code);
  if (idx >= 0) {
    base[idx] = { ...base[idx], state: code, licenseNumber: num };
  } else {
    base.push({ state: code, licenseNumber: num, primary: false });
  }
  return withPrimaryInvariant(base);
}

// Remove the license for a state (removes exactly ONE row — the first match).
// If it was primary, the next remaining row is promoted to primary. Matching a
// single row matters for the legacy state-less ("") rows: filtering by code
// equality would drop every state-less row at once. Returns a new array.
export function removeLicense(licenses, state) {
  const code = toCode(state);
  const arr = (Array.isArray(licenses) ? licenses : []).map((l) => ({ ...l }));
  const idx = arr.findIndex((l) => toCode(l.state) === code);
  if (idx === -1) return withPrimaryInvariant(arr);
  arr.splice(idx, 1);
  return withPrimaryInvariant(arr);
}

// Mark a state's license as primary (exactly ONE row — the first match), all
// others become non-primary. Falls back to the first row when the state isn't
// found. Single-row targeting keeps the one-primary invariant intact even when
// multiple state-less ("") rows are present.
export function setPrimaryLicense(licenses, state) {
  const code = toCode(state);
  const arr = (Array.isArray(licenses) ? licenses : []).map((l) => ({ ...l }));
  if (!arr.length) return [];
  const found = arr.findIndex((l) => toCode(l.state) === code);
  const targetIdx = found === -1 ? 0 : found;
  return arr.map((l, i) => ({ ...l, primary: i === targetIdx }));
}
